import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaSync, FaClock, FaCheckCircle, FaTimesCircle, FaInfoCircle, FaPlay, FaHistory, FaTimes, FaFilter, FaFileExport, FaTrash, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import Swal from 'sweetalert2';

const TallyOperationsPanel = () => {
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  const [tallyOperations, setTallyOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyHeadName, setCompanyHeadName] = useState('');
  const [runningSync, setRunningSync] = useState(null);
  const [selectedSyncLogs, setSelectedSyncLogs] = useState(null);
  const [selectedSyncName, setSelectedSyncName] = useState('');
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logFilters, setLogFilters] = useState({
    type: 'all',
    search: '',
    dateRange: {
      start: null,
      end: null
    }
  });
  const [logPagination, setLogPagination] = useState({
    currentPage: 1,
    logsPerPage: 10,
    totalPages: 1
  });
  const [logCounts, setLogCounts] = useState({
    total: 0,
    success: 0,
    info: 0,
    warning: 0,
    error: 0
  });

  // Sync dark mode with localStorage every 500ms
  useEffect(() => {
    const interval = setInterval(() => {
      const currentMode = localStorage.getItem('darkMode') === 'true';
      setDarkMode(currentMode);
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // Fetch tally operations
  useEffect(() => {
    fetchTallyOperations();
  }, []);

  const fetchTallyOperations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/tally-syncs/company-head-syncs`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setTallyOperations(response.data.data.syncs || []);
        setCompanyHeadName(response.data.data.companyHeadName || 'Company Head');
      }
    } catch (error) {
      console.error('Error fetching tally operations:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to fetch tally operations',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setLoading(false);
    }
  };

  const getSyncTypeLabel = (type) => {
    const typeMap = {
      'stock-summary': 'Stock Summary',
      'items': 'Items',
      'ledgers': 'Ledgers',
      'vouchers': 'Vouchers',
      'custom': 'Custom'
    };
    return typeMap[type] || type;
  };

  const getStatusBadge = (operation) => {
    // Check if there are any logs to determine last run
    if (!operation.logs || operation.logs.length === 0) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
          <FaInfoCircle className="mr-1" />
          Not Run Yet
        </span>
      );
    }

    // Get the latest log timestamp
    const lastLog = operation.logs[operation.logs.length - 1];
    const lastRunDate = new Date(lastLog.timestamp);
    const now = new Date();
    const hoursSinceLastRun = (now - lastRunDate) / (1000 * 60 * 60);

    if (hoursSinceLastRun < 24) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
          <FaCheckCircle className="mr-1" />
          Active
        </span>
      );
    } else if (hoursSinceLastRun < 48) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300">
          <FaClock className="mr-1" />
          Recent
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
          <FaTimesCircle className="mr-1" />
          Inactive
        </span>
      );
    }
  };

  const formatSchedule = (operation) => {
    if (!operation.type || operation.type === 'manual') {
      return 'Manual Sync';
    }

    if (operation.type === 'interval') {
      const duration = operation.duration || 0;
      const hours = Math.floor(duration / 60);
      const minutes = duration % 60;
      
      if (hours > 0 && minutes > 0) {
        return `Every ${hours}h ${minutes}m`;
      } else if (hours > 0) {
        return `Every ${hours}h`;
      } else {
        return `Every ${minutes}m`;
      }
    }

    if (operation.type === 'daily') {
      return `Daily at ${operation.time || 'scheduled time'}`;
    }

    return operation.type;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleRunSync = async (syncId) => {
    try {
      const result = await Swal.fire({
        title: 'Run Sync Operation?',
        text: 'This will trigger the sync operation manually.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, run it!'
      });

      if (result.isConfirmed) {
        setRunningSync(syncId);
        const token = localStorage.getItem('token');
        
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/tally-syncs/run/${syncId}`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        Swal.fire({
          icon: 'success',
          title: 'Sync Started!',
          text: 'The sync operation has been started successfully.',
          timer: 2000,
          showConfirmButton: false
        });

        // Refresh the operations list after a delay
        setTimeout(() => {
          fetchTallyOperations();
          setRunningSync(null);
        }, 2000);
      }
    } catch (error) {
      console.error('Error running sync:', error);
      setRunningSync(null);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to run sync operation',
        confirmButtonColor: '#3085d6'
      });
    }
  };

  const handleViewLogs = async (syncId, syncName) => {
    try {
      setSelectedSyncLogs(syncId);
      setSelectedSyncName(syncName);
      setLogPagination({ currentPage: 1, logsPerPage: 10, totalPages: 1 });
      setLogFilters({ type: 'all', search: '', dateRange: { start: null, end: null } });
      await fetchFilteredLogs(syncId);
    } catch (error) {
      console.error('Error opening logs modal:', error);
      setSelectedSyncLogs(null);
    }
  };

  const fetchFilteredLogs = async (syncId) => {
    try {
      setLogsLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        page: logPagination.currentPage.toString(),
        limit: logPagination.logsPerPage.toString(),
        type: logFilters.type,
        search: logFilters.search
      });

      if (logFilters.dateRange.start) {
        params.append('startDate', logFilters.dateRange.start);
      }
      if (logFilters.dateRange.end) {
        params.append('endDate', logFilters.dateRange.end);
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/tally-syncs/${syncId}/logs?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setLogs(response.data.logs || []);
      setLogCounts(response.data.logCounts || { total: 0, success: 0, info: 0, warning: 0, error: 0 });
      setLogPagination(prev => ({
        ...prev,
        totalPages: response.data.pagination?.totalPages || 1
      }));
    } catch (error) {
      console.error('Error fetching logs:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to fetch logs',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setLogsLoading(false);
    }
  };

  const handleCloseLogs = () => {
    setSelectedSyncLogs(null);
    setSelectedSyncName('');
    setLogs([]);
    setLogFilters({ type: 'all', search: '', dateRange: { start: null, end: null } });
    setLogPagination({ currentPage: 1, logsPerPage: 10, totalPages: 1 });
  };

  const handlePageChange = (pageNumber) => {
    setLogPagination(prev => ({ ...prev, currentPage: pageNumber }));
  };

  const handleFilterChange = (newFilters) => {
    setLogFilters(newFilters);
    setLogPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleExportLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        type: logFilters.type,
        search: logFilters.search
      });

      if (logFilters.dateRange.start) {
        params.append('startDate', logFilters.dateRange.start);
      }
      if (logFilters.dateRange.end) {
        params.append('endDate', logFilters.dateRange.end);
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/tally-syncs/${selectedSyncLogs}/logs?${params}&page=1&limit=10000`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const allLogs = response.data.logs || [];
      
      // Create CSV content
      const csvContent = [
        ['Timestamp', 'Type', 'Message'].join(','),
        ...allLogs.map(log => {
          const logType = getLogTypeColor(log).type;
          const timestamp = formatDateTime(log.timestamp);
          const message = `"${log.message.replace(/"/g, '""')}"`;
          return [timestamp, logType, message].join(',');
        })
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `tally-sync-logs-${selectedSyncName}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      Swal.fire({
        icon: 'success',
        title: 'Exported!',
        text: 'Logs exported successfully',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error exporting logs:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to export logs',
        confirmButtonColor: '#3085d6'
      });
    }
  };

  const handleDeleteAllLogs = async () => {
    try {
      const result = await Swal.fire({
        title: 'Delete All Logs?',
        text: 'This will permanently delete all logs for this sync operation. This action cannot be undone!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete all logs!'
      });

      if (result.isConfirmed) {
        const token = localStorage.getItem('token');
        await axios.delete(
          `${process.env.REACT_APP_API_BASE_URL}/tally-syncs/${selectedSyncLogs}/logs`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        await Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'All logs have been deleted.',
          timer: 2000,
          showConfirmButton: false
        });

        // Refresh logs
        await fetchFilteredLogs(selectedSyncLogs);
      }
    } catch (error) {
      console.error('Error deleting logs:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to delete logs',
        confirmButtonColor: '#3085d6'
      });
    }
  };

  // Re-fetch logs when pagination or filters change
  useEffect(() => {
    if (selectedSyncLogs) {
      fetchFilteredLogs(selectedSyncLogs);
    }
  }, [logPagination.currentPage, logPagination.logsPerPage, logFilters]);

  const getLogTypeColor = (log) => {
    const message = log.message?.toLowerCase() || '';
    
    // Detect type based on message content
    if ((message.includes('error') || message.includes('failed') || message.includes('timeout')) 
        && !message.includes('errored: 0')) {
      return { 
        color: 'text-red-600 dark:text-red-300', 
        bgColor: 'bg-red-100 dark:bg-red-900/30',
        type: 'ERROR' 
      };
    } else if (message.includes('warning') || message.includes('attention') || 
               message.includes('could not') || message.includes('retry')) {
      return { 
        color: 'text-yellow-600 dark:text-yellow-300', 
        bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
        type: 'WARNING' 
      };
    } else if (message.includes('completed') || message.includes('updated:') || 
               message.includes('created:') || message.includes('sync started') ||
               message.includes('tally is active') || message.includes('verified') ||
               message.includes('parsed') || (message.includes('errored: 0') && 
               (message.includes('updated:') || message.includes('created:')))) {
      return { 
        color: 'text-green-600 dark:text-green-300', 
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        type: 'SUCCESS' 
      };
    }
    
    return { 
      color: 'text-blue-600 dark:text-blue-300', 
      bgColor: 'bg-blue-100 dark:bg-blue-900/30',
      type: 'INFO' 
    };
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-gray-50 to-gray-100'} p-6`}>
        <div className="flex items-center justify-center h-screen">
          <div className="flex flex-col items-center">
            <FaSync className={`animate-spin text-6xl ${darkMode ? 'text-blue-400' : 'text-blue-600'} mb-6`} />
            <p className={`text-xl font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Loading tally operations...</p>
            <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Please wait while we fetch the data</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-gray-50 to-gray-100'} p-6`}>
      <div className="w-full mx-auto">
        {/* Header */}
        <div className={`${darkMode ? 'bg-gray-800/80 backdrop-blur-sm border border-gray-700/50' : 'bg-white'} rounded-xl shadow-lg p-6 mb-6`}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Tally Operations Panel
              </h1>
              <p className={`mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Monitor Tally sync operations managed by <span className="font-semibold">{companyHeadName}</span>
              </p>
            </div>
            <button
              onClick={fetchTallyOperations}
              className={`flex items-center px-5 py-2.5 rounded-lg font-medium transition-all ${
                darkMode
                  ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50'
                  : 'bg-blue-500 hover:bg-blue-600 text-white shadow-md hover:shadow-lg'
              }`}
            >
              <FaSync className="mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {/* Operations List */}
        {tallyOperations.length === 0 ? (
          <div className={`${darkMode ? 'bg-gray-800/80 backdrop-blur-sm border border-gray-700/50' : 'bg-white'} rounded-xl shadow-lg p-12 text-center`}>
            <FaInfoCircle className={`mx-auto text-6xl mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
            <h3 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              No Tally Operations Found
            </h3>
            <p className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Your company head hasn't created any tally sync operations yet.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tallyOperations.map((operation) => (
              <div
                key={operation._id}
                className={`${
                  darkMode ? 'bg-gray-800/80 backdrop-blur-sm border-gray-700/50' : 'bg-white border-gray-200'
                } rounded-xl shadow-lg border hover:shadow-2xl ${darkMode ? 'hover:shadow-blue-500/20' : ''} transition-all transform hover:-translate-y-1`}
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-1`}>
                        {operation.name}
                      </h3>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {getSyncTypeLabel(operation.TallySyncType)}
                      </p>
                    </div>
                    {getStatusBadge(operation)}
                  </div>

                  {/* Details */}
                  <div className="space-y-3">
                    <div>
                      <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>
                        Schedule
                      </p>
                      <div className={`flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <FaClock className="mr-2 text-sm" />
                        <span className="text-sm">{formatSchedule(operation)}</span>
                      </div>
                    </div>

                    {operation.logs && operation.logs.length > 0 && (
                      <div>
                        <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>
                          Last Run
                        </p>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {formatDateTime(operation.logs[operation.logs.length - 1].timestamp)}
                        </p>
                      </div>
                    )}

                    {operation.nextRun && (
                      <div>
                        <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>
                          Next Scheduled Run
                        </p>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {formatDateTime(operation.nextRun)}
                        </p>
                      </div>
                    )}

                    <div>
                      <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>
                        Created On
                      </p>
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {formatDateTime(operation.createdAt)}
                      </p>
                    </div>

                    {/* Latest Log */}
                    {operation.logs && operation.logs.length > 0 && (
                      <div>
                        <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-2`}>
                          Latest Log
                        </p>
                        <div className={`p-3 rounded-lg border ${darkMode ? 'bg-gray-900/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="flex items-start justify-between mb-2">
                            <span className={`text-xs font-semibold px-2 py-1 rounded ${getLogTypeColor(operation.logs[operation.logs.length - 1]).bgColor} ${getLogTypeColor(operation.logs[operation.logs.length - 1]).color}`}>
                              {getLogTypeColor(operation.logs[operation.logs.length - 1]).type}
                            </span>
                            <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {formatDateTime(operation.logs[operation.logs.length - 1].timestamp)}
                            </span>
                          </div>
                          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed`}>
                            {operation.logs[operation.logs.length - 1].message}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-6 flex gap-3">
                    <button
                      onClick={() => handleRunSync(operation._id)}
                      disabled={runningSync === operation._id}
                      className={`flex-1 flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold transition-all shadow-md ${
                        runningSync === operation._id
                          ? darkMode ? 'bg-gray-600 text-gray-400 cursor-not-allowed' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : darkMode
                          ? 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg hover:shadow-blue-500/30'
                          : 'bg-blue-500 hover:bg-blue-600 text-white hover:shadow-lg'
                      }`}
                    >
                      {runningSync === operation._id ? (
                        <>
                          <FaSync className="mr-2 animate-spin" />
                          Running...
                        </>
                      ) : (
                        <>
                          <FaPlay className="mr-2" />
                          Run Sync
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleViewLogs(operation._id, operation.name)}
                      className={`flex-1 flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold transition-all shadow-md ${
                        darkMode
                          ? 'bg-gray-700 hover:bg-gray-600 text-white hover:shadow-lg'
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-800 hover:shadow-md'
                      }`}
                    >
                      <FaHistory className="mr-2" />
                      View Logs
                    </button>
                  </div>
                </div>

                {/* Footer */}
                <div className={`px-6 py-3.5 ${darkMode ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'} border-t rounded-b-xl`}>
                  <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    ID: <span className={`font-mono ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{operation.id || operation._id}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Logs Modal */}
        {selectedSyncLogs && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className={`${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'} rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col`}>
              {/* Modal Header */}
              <div className={`flex items-center justify-between p-6 border-b ${darkMode ? 'border-gray-700 bg-gradient-to-r from-gray-800 to-gray-750' : 'border-gray-200 bg-gradient-to-r from-gray-50 to-white'} rounded-t-2xl`}>
                <div>
                  <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Sync Logs - {selectedSyncName}
                  </h3>
                  <div className="flex gap-5 mt-3">
                    <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Total: <strong className={darkMode ? 'text-white' : 'text-gray-900'}>{logCounts.total}</strong>
                    </span>
                    <span className="text-sm font-medium text-green-600 dark:text-green-400">
                      Success: <strong>{logCounts.success}</strong>
                    </span>
                    <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400">
                      Warning: <strong>{logCounts.warning}</strong>
                    </span>
                    <span className="text-sm font-medium text-red-600 dark:text-red-400">
                      Error: <strong>{logCounts.error}</strong>
                    </span>
                    <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                      Info: <strong>{logCounts.info}</strong>
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleCloseLogs}
                  className={`p-2.5 rounded-lg transition-all ${
                    darkMode ? 'hover:bg-gray-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <FaTimes className="text-xl" />
                </button>
              </div>

              {/* Filters */}
              <div className={`p-5 border-b ${darkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex flex-wrap gap-3">
                  {/* Type Filter */}
                  <select
                    value={logFilters.type}
                    onChange={(e) => handleFilterChange({ ...logFilters, type: e.target.value })}
                    className={`px-4 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-650 focus:ring-2 focus:ring-blue-500'
                        : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400 focus:ring-2 focus:ring-blue-500'
                    }`}
                  >
                    <option value="all">All Types</option>
                    <option value="success">✓ Success</option>
                    <option value="warning">⚠ Warning</option>
                    <option value="error">✗ Error</option>
                    <option value="info">ℹ Info</option>
                  </select>

                  {/* Search Filter */}
                  <input
                    type="text"
                    placeholder="Search logs..."
                    value={logFilters.search}
                    onChange={(e) => handleFilterChange({ ...logFilters, search: e.target.value })}
                    className={`flex-1 min-w-[200px] px-4 py-2.5 rounded-lg border text-sm transition-all ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500'
                    }`}
                  />

                  {/* Date Range */}
                  <input
                    type="date"
                    value={logFilters.dateRange.start || ''}
                    onChange={(e) => handleFilterChange({ 
                      ...logFilters, 
                      dateRange: { ...logFilters.dateRange, start: e.target.value } 
                    })}
                    className={`px-4 py-2.5 rounded-lg border text-sm transition-all ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white focus:ring-2 focus:ring-blue-500'
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500'
                    }`}
                  />
                  <input
                    type="date"
                    value={logFilters.dateRange.end || ''}
                    onChange={(e) => handleFilterChange({ 
                      ...logFilters, 
                      dateRange: { ...logFilters.dateRange, end: e.target.value } 
                    })}
                    className={`px-4 py-2.5 rounded-lg border text-sm transition-all ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white focus:ring-2 focus:ring-blue-500'
                        : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500'
                    }`}
                  />

                  {/* Action Buttons */}
                  <button
                    onClick={handleExportLogs}
                    className={`flex items-center px-4 py-2.5 rounded-lg text-sm font-medium transition-all shadow-md ${
                      darkMode
                        ? 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg hover:shadow-blue-500/30'
                        : 'bg-blue-500 hover:bg-blue-600 text-white hover:shadow-lg'
                    }`}
                  >
                    <FaFileExport className="mr-2" />
                    Export
                  </button>
                  <button
                    onClick={handleDeleteAllLogs}
                    className={`flex items-center px-4 py-2.5 rounded-lg text-sm font-medium transition-all shadow-md ${
                      darkMode
                        ? 'bg-red-600 hover:bg-red-700 text-white hover:shadow-lg hover:shadow-red-500/30'
                        : 'bg-red-500 hover:bg-red-600 text-white hover:shadow-lg'
                    }`}
                  >
                    <FaTrash className="mr-2" />
                    Delete All
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto p-6">
                {logsLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <FaSync className={`animate-spin text-4xl ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>
                ) : logs.length === 0 ? (
                  <div className="text-center py-16">
                    <FaInfoCircle className={`mx-auto text-5xl mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                    <p className={`text-lg font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      No logs available
                    </p>
                    <p className={`text-sm mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      No logs found for this sync operation with the current filters.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {logs.map((log, index) => {
                      const logTypeInfo = getLogTypeColor(log);
                      return (
                        <div
                          key={index}
                          className={`p-4 rounded-xl border transition-all hover:shadow-md ${
                            darkMode ? 'bg-gray-800/60 border-gray-700/50 hover:bg-gray-800' : 'bg-white border-gray-200 hover:shadow-lg'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className={`text-xs font-bold px-3 py-1 rounded-full ${logTypeInfo.bgColor} ${logTypeInfo.color}`}>
                              {logTypeInfo.type}
                            </span>
                            <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {formatDateTime(log.timestamp)}
                            </span>
                          </div>
                          <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                            {log.message}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Modal Footer with Pagination */}
              <div className={`flex items-center justify-between p-6 border-t ${darkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'} rounded-b-2xl`}>
                {/* Pagination Info */}
                <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Showing page <span className="font-bold">{logPagination.currentPage}</span> of <span className="font-bold">{logPagination.totalPages}</span>
                </div>

                {/* Pagination Controls */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(logPagination.currentPage - 1)}
                    disabled={logPagination.currentPage === 1}
                    className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      logPagination.currentPage === 1
                        ? darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : darkMode
                        ? 'bg-gray-700 hover:bg-gray-600 text-white shadow-md hover:shadow-lg'
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-800 shadow-sm hover:shadow-md'
                    }`}
                  >
                    <FaChevronLeft className="mr-1" />
                    Previous
                  </button>

                  {/* Page Numbers */}
                  <div className="flex gap-2">
                    {[...Array(Math.min(5, logPagination.totalPages))].map((_, idx) => {
                      let pageNum;
                      if (logPagination.totalPages <= 5) {
                        pageNum = idx + 1;
                      } else if (logPagination.currentPage <= 3) {
                        pageNum = idx + 1;
                      } else if (logPagination.currentPage >= logPagination.totalPages - 2) {
                        pageNum = logPagination.totalPages - 4 + idx;
                      } else {
                        pageNum = logPagination.currentPage - 2 + idx;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-10 h-10 rounded-lg text-sm font-bold transition-all ${
                            logPagination.currentPage === pageNum
                              ? darkMode
                                ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                                : 'bg-blue-500 text-white shadow-md'
                              : darkMode
                              ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white'
                              : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(logPagination.currentPage + 1)}
                    disabled={logPagination.currentPage === logPagination.totalPages}
                    className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      logPagination.currentPage === logPagination.totalPages
                        ? darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : darkMode
                        ? 'bg-gray-700 hover:bg-gray-600 text-white shadow-md hover:shadow-lg'
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-800 shadow-sm hover:shadow-md'
                    }`}
                  >
                    Next
                    <FaChevronRight className="ml-1" />
                  </button>
                </div>

                {/* Close Button */}
                <button
                  onClick={handleCloseLogs}
                  className={`px-5 py-2 rounded-lg font-medium transition-all shadow-md ${
                    darkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-white hover:shadow-lg'
                      : 'bg-gray-300 hover:bg-gray-400 text-gray-800 hover:shadow-lg'
                  }`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TallyOperationsPanel;
